<template>
  <div class="members-layout">
    <!-- 主内容区 -->
    <main class="members-content">
      <!-- 搜索和筛选区域 -->
      <div class="filter-section">
        <div class="filter-header">
          <h1 class="page-title">会员管理</h1>
          <button class="add-member-button" @click="openAddMemberForm">
            + 添加会员
          </button>
        </div>
        <div class="filter-controls">
          <div class="search-bar">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索会员姓名或手机号..."
              class="search-input"
              @keyup.enter="handleSearch"
            />
            <button class="search-button" @click="handleSearch">搜索</button>
            <button
              class="refresh-members-btn"
              @click="refreshMembers"
              :disabled="loading"
              title="刷新会员列表"
            >
              <img
                src="/img/refresh.png"
                alt="刷新"
                class="refresh-icon"
                :class="{ 'spinning': loading }"
              >
            </button>
          </div>
          <button class="reset-button" @click="resetFilters">重置</button>
        </div>
      </div>

      <!-- 会员统计 -->
      <div class="members-stats">
        <div class="stat-card">
          <div class="stat-label">会员总数</div>
          <div class="stat-value">{{ total }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">订单总额</div>
          <div class="stat-value">¥{{ totalOrderAmount }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">新增会员</div>
          <div class="stat-value">{{ newMembersCount }}</div>
        </div>
      </div>

      <!-- 会员表格 -->
      <div class="members-table-container">
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">加载中...</div>
        </div>
        <table v-else class="members-table">
          <thead>
            <tr>
              <th>会员信息</th>
              <th>手机号</th>
              <th class="sortable-header" @click="toggleOrderCountSort">
                订单数量
                <span class="sort-indicator">
                  <span v-if="orderCountSortOrder === 'asc'" class="sort-asc">↑</span>
                  <span v-else-if="orderCountSortOrder === 'desc'" class="sort-desc">↓</span>
                  <span v-else class="sort-none">↕</span>
                </span>
              </th>
              <th class="sortable-header" @click="toggleOrderAmountSort">
                订单总额
                <span class="sort-indicator">
                  <span v-if="orderAmountSortOrder === 'asc'" class="sort-asc">↑</span>
                  <span v-else-if="orderAmountSortOrder === 'desc'" class="sort-desc">↓</span>
                  <span v-else class="sort-none">↕</span>
                </span>
              </th>
              <th>注册时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedMembers.length === 0">
              <td colspan="6" class="no-data">暂无会员数据</td>
            </tr>
            <tr v-for="member in paginatedMembers" :key="member.id" class="member-row">
              <td class="member-info">
                <div class="member-name">{{ member.name }}</div>
              </td>
              <td class="member-phone">{{ member.phone }}</td>
              <td class="order-count-cell">
                <span class="order-count">{{ member.orderCount }}</span>
              </td>
              <td class="order-amount-cell">¥{{ member.orderAmount }}</td>
              <td class="register-date-cell">{{ member.registerDate }}</td>
              <td class="action-cell">
                <button class="edit-button" @click="openEditMemberForm(member)">
                  修改
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <div class="pagination-info">
          显示 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, total) }}
          共 {{ total }} 条
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            上一页
          </button>
          <span class="page-number">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="page-btn"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            下一页
          </button>
        </div>
      </div>
    </main>

    <!-- 会员表单弹窗 -->
    <MemberForm
      :visible="showMemberForm"
      :member="selectedMember"
      @close="closeMemberForm"
      @submit="handleMemberSubmit"
    />
  </div>
</template>

<script>
import MemberForm from './MemberForm.vue'
import { memberApi } from '../api/index.js'

export default {
  name: 'MembersView',
  components: {
    MemberForm
  },
  props: {
    userInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      searchKeyword: '',
      currentPage: 1,
      orderCountSortOrder: null, // null, 'asc', 'desc'
      orderAmountSortOrder: null, // null, 'asc', 'desc'
      pageSize: 15,
      showMemberForm: false,
      selectedMember: null,
      members: [],
      loading: false,
      total: 0 // 总记录数
    }
  },
  computed: {
    // 由于现在通过API直接获取分页数据，paginatedMembers直接返回members
    paginatedMembers() {
      return this.members || []
    },
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    totalOrderAmount() {
      // 由于API返回的数据中没有订单统计，这里显示为0
      // 实际项目中需要另外的API来获取订单统计
      return 0
    },
    newMembersCount() {
      const currentMonth = new Date().getMonth()
      const currentYear = new Date().getFullYear()

      return this.members.filter(member => {
        if (!member.created_at) return false
        const registerDate = new Date(member.created_at)
        return registerDate.getMonth() === currentMonth && registerDate.getFullYear() === currentYear
      }).length
    }
  },
  methods: {
    // 加载会员列表
    async loadMembers() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }

        // 添加搜索参数
        if (this.searchKeyword.trim()) {
          params.name = this.searchKeyword.trim()
        }

        const result = await memberApi.getMemberList(params)

        if (result.code === 0 && result.data) {
          // 转换数据格式
          this.members = result.data.list.map(member => ({
            id: member.ID,
            name: member.name,
            phone: member.phone,
            gender: this.mapGenderToChinese(member.gender),
            address: member.address || '无地址',
            registerDate: member.created_at ? new Date(member.created_at).toISOString().split('T')[0] : '未知',
            orderCount: 0, // API暂不提供，设为默认值
            orderAmount: 0  // API暂不提供，设为默认值
          }))
          this.total = result.data.total || 0
          console.log('会员列表加载成功:', this.members)
        } else {
          console.error('获取会员列表失败:', result.msg)
          alert(result.msg || '获取会员列表失败')
        }
      } catch (error) {
        console.error('获取会员列表异常:', error)
        alert('获取会员列表失败，请检查网络连接')
      } finally {
        this.loading = false
      }
    },

    // 性别映射：后端字段 -> 前端显示
    mapGenderToChinese(gender) {
      const genderMap = {
        'male': '男',
        'female': '女',
        'other': '未知'
      }
      return genderMap[gender] || '未知'
    },

    // 性别映射：前端显示 -> 后端字段
    mapGenderToEnglish(gender) {
      const genderMap = {
        '男': 'male',
        '女': 'female',
        '未知': 'other',
        '': 'other'
      }
      return genderMap[gender] || 'other'
    },

    toggleOrderCountSort() {
      // 由于API没有提供订单统计，暂时禁用排序功能
      alert('排序功能暂时不可用')
    },
    toggleOrderAmountSort() {
      // 由于API没有提供订单统计，暂时禁用排序功能
      alert('排序功能暂时不可用')
    },
    async handleSearch() {
      this.currentPage = 1 // 重置到第一页
      await this.loadMembers() // 重新加载数据
    },
    resetFilters() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.loadMembers() // 重新加载数据
    },
    openAddMemberForm() {
      this.selectedMember = null
      this.showMemberForm = true
    },
    openEditMemberForm(member) {
      this.selectedMember = member
      this.showMemberForm = true
    },
    closeMemberForm() {
      this.showMemberForm = false
      this.selectedMember = null
    },
    async handleMemberSubmit(memberData) {
      try {
        const submitData = {
          ...memberData,
          gender: this.mapGenderToEnglish(memberData.gender)
        }

        if (this.selectedMember) {
          // 修改会员
          const result = await memberApi.updateMember(submitData)
          if (result.code === 0) {
            alert('会员信息更新成功')
            await this.loadMembers() // 重新加载列表
          } else {
            alert(result.msg || '更新会员失败')
          }
        } else {
          // 添加会员
          const result = await memberApi.addMember(submitData)
          if (result.code === 0) {
            alert('会员添加成功')
            await this.loadMembers() // 重新加载列表
          } else {
            alert(result.msg || '添加会员失败')
          }
        }
        this.currentPage = 1
      } catch (error) {
        console.error('保存会员失败:', error)
        alert('保存失败，请检查网络连接')
      }
    },

    // 刷新会员列表
    async refreshMembers() {
      console.log('刷新会员列表...')
      try {
        // 重置到第一页并重新加载
        this.currentPage = 1
        await this.loadMembers()
        alert('会员列表已刷新')
      } catch (error) {
        console.error('刷新会员列表失败:', error)
        alert('刷新会员列表失败')
      }
    }
  },
  watch: {
    currentPage() {
      this.loadMembers()
    }
  },
  mounted() {
    this.loadMembers()
  }
}
</script>

<style scoped>
.members-layout {
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Microsoft YaHei', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 主内容区 */
.members-content {
  flex: 1;
  height: 100vh;
  padding: 24px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-sizing: border-box;
  min-width: 0;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 24px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.filter-header {
  margin-bottom: 20px;
}

.page-title {
  margin: 0 0 20px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.5px;
  position: relative;
  padding-left: 20px;
}

.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 28px;
  background: linear-gradient(180deg, #667eea, #764ba2);
  border-radius: 2px;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.add-member-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 120px;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.add-member-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 450px;
}

.search-input {
  border: 2px solid #e1e5e9;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  font-size: 14px;
  width: 100%;
  outline: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #f8fafc;
  color: #334155;
  font-weight: 500;
}

.search-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: white;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 80px;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.search-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.refresh-members-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #52c41a, #389e0d);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(82, 196, 26, 0.3);
  padding: 0;
  flex-shrink: 0;
}

.refresh-members-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(82, 196, 26, 0.4);
}

.refresh-members-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.refresh-members-btn .refresh-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.9;
  transition: all 0.3s ease;
}

.refresh-members-btn:hover:not(:disabled) .refresh-icon {
  opacity: 1;
}

.refresh-members-btn .refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

.reset-button {
  background: #f1f5f9;
  color: #64748b;
  border: 2px solid #e2e8f0;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 80px;
  white-space: nowrap;
}

.reset-button:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  color: #475569;
  transform: translateY(-1px);
}

/* 统计卡片 */
.members-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.stat-value {
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 表格 */
.members-table-container {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.4);
  margin-bottom: 24px;
}

.members-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.members-table th {
  background: #f8fafc;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e2e8f0;
  font-size: 13px;
  white-space: nowrap;
  position: relative;
}

.members-table th:nth-child(1) {
  width: 25%;
}

.members-table th:nth-child(2) {
  width: 20%;
}

.members-table th:nth-child(3) {
  width: 15%;
}

.members-table th:nth-child(4) {
  width: 20%;
}

.members-table th:nth-child(5) {
  width: 15%;
}

.members-table th:nth-child(6) {
  width: 10%;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: all 0.3s ease;
}

.sortable-header:hover {
  background: #e2e8f0;
}

.sort-indicator {
  margin-left: 8px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.sort-asc { color: #10b981; }
.sort-desc { color: #ef4444; }
.sort-none { color: #94a3b8; }

.members-table td {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  font-size: 14px;
  color: #374151;
}

.member-row {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.member-row:hover {
  background: rgba(102, 126, 234, 0.05);
  transform: scale(1.005);
}

.member-row:hover::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #667eea, #764ba2);
}

.member-info {
  min-width: 120px;
}

.member-name {
  font-weight: 500;
  color: #333;
  line-height: 1.4;
}

.member-phone {
  color: #666;
  font-size: 14px;
  font-family: monospace;
}

.order-count-cell {
  text-align: center;
  font-weight: 600;
}

.order-count {
  background-color: #e6f7ff;
  color: #1890ff;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.order-amount-cell {
  text-align: right;
  font-weight: 600;
  color: #e74c3c;
}

.register-date-cell {
  color: #666;
  font-size: 13px;
}

.action-cell {
  text-align: center;
}

.edit-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.edit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

.no-data {
  text-align: center;
  color: #64748b;
  padding: 60px;
  font-size: 16px;
  font-weight: 600;
  background: rgba(248, 250, 252, 0.6);
  border-radius: 16px;
  margin: 20px;
  border: 2px dashed rgba(102, 126, 234, 0.2);
}

.loading-data {
  text-align: center;
  color: #64748b;
  padding: 60px;
  font-size: 16px;
  font-weight: 600;
}

.loading-spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid rgba(102, 126, 234, 0.1);
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  margin-right: 12px;
  vertical-align: middle;
  position: relative;
}

.loading-spinner::after {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #764ba2;
  animation: spin 1.5s cubic-bezier(0.4, 0, 0.2, 1) reverse infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 分页 */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 24px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-sizing: border-box;
}

.pagination-info {
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-btn {
  background: white;
  border: 2px solid #e5e7eb;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.page-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.page-number {
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  padding: 8px 16px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 12px;
  min-width: 60px;
  text-align: center;
}


/* 响应式优化 */
@media (max-width: 1200px) {
  .members-stats {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .stat-card {
    padding: 20px 16px;
  }

  .stat-value {
    font-size: 28px;
  }
}

@media (max-width: 768px) {
  .members-content {
    padding: 16px;
  }

  .filter-section {
    padding: 20px;
  }

  .filter-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .filter-controls {
    flex-direction: column;
    gap: 12px;
  }

  .search-bar {
    flex-direction: column;
    gap: 8px;
  }

  .search-input {
    width: 100%;
  }

  .members-stats {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .members-table-container {
    overflow-x: auto;
  }

  .pagination-container {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
}
</style>